/**
 * IntegrationListingFilters — Filter configuration hook
 *
 * Follows the same pattern as EPDashboard/EPList/Filters.config.tsx:
 *  - Reads initial filter values from URL query params on mount.
 *  - Uses `FilterConfig` from SearchAndFilters to declare each filter.
 *  - Integrates with Jotai atoms so the grid reacts automatically.
 *
 * Supported filters:
 *   provider, module, integration_type, account_id, search, last_sync_at
 */

import {
  DateRangeFilterConfig,
  FilterConfig,
  FilterGroupConfig,
  InputFilterConfig,
  SearchInputFilterConfig,
} from 'components/SearchAndFilters/FilterTypes';
import {
  MultiSelectFilterConfig,
  MultiSelectRenderStyle,
} from 'components/SearchAndFilters/MultiSelect';
import { useAtomValue, useSetAtom } from 'jotai';
import { showFailureToast } from 'lib/ToastHelper';
import React, { useEffect, useState } from 'react';
import { replaceBrowserSearch } from 'views/bpaUrlFilterUtils';
import { companyIdAtom } from 'views/state';

import { fetchIntegrationOptions } from '../../services/integrationListingService';
import {
  filterValuesAtom,
  integrationOptionsDataAtom,
  isQuerystringFiltersInitializedAtom,
  parsedFilterValuesAtom,
} from '../../state';
import { parseBPAIntegrationUrlFilters } from '../../urlFilters';

export const useIntegrationFilterConfig = (): FilterConfig => {
  const setFilterValues = useSetAtom(filterValuesAtom);
  const parsedFilterValues = useAtomValue(parsedFilterValuesAtom);
  const setQuerystringFiltersInitialized = useSetAtom(
    isQuerystringFiltersInitializedAtom,
  );
  const companyId = useAtomValue(companyIdAtom);

  const setIntegrationOptionsData = useSetAtom(integrationOptionsDataAtom);

  const [providerOptions, setProviderOptions] = useState<
    { title: string; value: string }[]
  >([]);
  const [moduleOptions, setModuleOptions] = useState<
    { title: string; value: string }[]
  >([]);
  const [_regionOptions, setRegionOptions] = useState<
    { title: string; value: string }[]
  >([]);

  useEffect(() => {
    if (!companyId || companyId === -1) return;
    fetchIntegrationOptions(companyId, 'providers,modules,regions,benchmarks')
      .then((res) => {
        if (res.status === 200 && res.data) {
          const providers = res.data.providers || [];
          const modules = res.data.modules || [];
          const rawRegions: Array<
            Record<string, { title: string; value: string }[]>
          > = res.data.regions || [];
          const benchmarks = res.data.benchmarks || [];

          setProviderOptions(providers);
          setModuleOptions(modules);
          setRegionOptions(
            rawRegions.flatMap((group) => Object.values(group).flat()),
          );

          setIntegrationOptionsData({
            providers,
            modules,
            regions: rawRegions,
            benchmarks,
          });
        }
      })
      .catch((err) => {
        let error = 'Error occurred. Please try again.';
        if (err?.data?.detail) {
          if (err.data.detail.includes('duplicate')) {
            error =
              'An integration label with the same name is already assigned. Please choose a different name.';
          } else {
            error = err.data.detail;
          }
        }
        showFailureToast(error);
      });
  }, [companyId, setIntegrationOptionsData]);

  // ── Hydrate filters from URL query string on first render ──────────────────
  useEffect(() => {
    setQuerystringFiltersInitialized(false);

    const hydration = parseBPAIntegrationUrlFilters(window.location.search);

    // Always replace route-scoped state so a clean URL cannot inherit filters
    // from an earlier SPA visit. URL filters win; defaults are fallback only.
    setFilterValues(hydration.filterValues);
    replaceBrowserSearch(hydration.canonicalSearch);

    setQuerystringFiltersInitialized(true);

    return () => {
      setQuerystringFiltersInitialized(false);
    };
    // Run once on mount — no deps needed
  }, [setFilterValues, setQuerystringFiltersInitialized]);

  // ── Build the FilterConfig (memoised to avoid recreation on every render) ──
  const config = React.useMemo(() => {
    const newConfig = new FilterConfig({
      searchFilterId: 'search',
      rightPanelFilters: [
        'status',
        ...(providerOptions.length > 1 ? ['provider'] : []),
        ...(moduleOptions.length > 1 ? ['module'] : []),
        'integration_type',
      ],
      leftPanelFilters: ['dateRangeFilters', 'account_id'],
      filters: [
        // ── Search ───────────────────────────────────────────────────────
        new SearchInputFilterConfig({
          id: 'search',
          label: 'Search',
          placeholder: 'Search integrations…',
          maxLength: 250,
          debounceTime: 600,
          enableDebounce: true,
          inputProps: { type: 'text' },
          showInFilterMenu: false,
        }),

        // ── Provider ─────────────────────────────────────────────────────
        ...(providerOptions.length > 1
          ? [
              new MultiSelectFilterConfig({
                id: 'provider',
                label: 'Provider',
                placeholder: 'Select provider',
                tooltip: 'Filter by cloud provider.',
                iconClass: 'fas fa-cloud',
                defaultSelectedValues: [''],
                options: [
                  { label: 'All', value: '', deselectOthers: true },
                  ...providerOptions.map((o) => ({
                    label: o.value,
                    value: o.value,
                  })),
                ],
                renderStyle: MultiSelectRenderStyle.Badges,
              }),
            ]
          : []),

        // ── Module ───────────────────────────────────────────────────────
        ...(moduleOptions.length > 1
          ? [
              new MultiSelectFilterConfig({
                id: 'module',
                label: 'Module',
                placeholder: 'Select module',
                tooltip: 'Filter by security module.',
                iconClass: 'fas fa-cube',
                defaultSelectedValues: [''],
                options: [
                  { label: 'All', value: '', deselectOthers: true },
                  ...moduleOptions.map((o) => ({
                    label: o.value,
                    value: o.value,
                  })),
                ],
                renderStyle: MultiSelectRenderStyle.Badges,
              }),
            ]
          : []),

        // ── Integration Type ─────────────────────────────────────────────
        new MultiSelectFilterConfig({
          id: 'integration_type',
          label: 'Integration Level',
          placeholder: 'Select type',
          tooltip: 'Filter by integration scope.',
          iconClass: 'fas fa-plug',
          defaultSelectedValues: [''],
          options: [
            { label: 'All', value: '', deselectOthers: true },
            {
              label: 'Account Level',
              value: 'ACCOUNT_LEVEL',
              deselectOthers: true,
            },
            {
              label: 'Organization Level',
              value: 'ORGANIZATION_LEVEL',
              deselectOthers: true,
            },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),

        // ── Status ───────────────────────────────────────────────────────
        new MultiSelectFilterConfig({
          id: 'status',
          label: 'Status',
          tooltip: 'Filter by integration status.',
          iconClass: 'fas fa-clipboard-check',
          defaultSelectedValues: [''],
          options: [
            {
              value: '',
              label: 'All',
              deselectOthers: true,
            },
            {
              value: 'active',
              label: 'Active',
              deselectOthers: true,
            },
            {
              value: 'aging',
              label: 'Only In Aging Out Process',
              deselectOthers: true,
            },
            {
              value: 'deleted',
              label: 'Deleted (Aged Out)',
              deselectOthers: true,
            },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),

        // ── Account ID (free-text input) ───────────────────────────────
        new InputFilterConfig({
          id: 'account_id',
          label: 'Account ID',
          tooltip: 'Filter by cloud account ID.',
          iconClass: 'fas fa-id-badge',
          inputProps: {
            type: 'number',
            placeholder: 'Account ID',
            min: 1,
            step: 1,
          },
          debounceTime: 600,
          enableDebounce: true,
        }),

        // ── Date Range Filters ──────────────────────────────────────────
        new FilterGroupConfig({
          id: 'dateRangeFilters',
          label: 'Filter by Date',
          tooltip: 'Filter by created, updated or last modified dates.',
          iconClass: 'fas fa-calendar-alt',
          filters: [
            new DateRangeFilterConfig({
              id: 'created_at',
              label: 'Created At',
            }),
            new DateRangeFilterConfig({
              id: 'updated_at',
              label: 'Updated At',
            }),
            new DateRangeFilterConfig({
              id: 'last_sync_at',
              label: 'Last Sync At',
            }),
          ],
        }),
      ],
    });
    newConfig.applyInitialFilterValues(parsedFilterValues);
    return newConfig;
  }, [parsedFilterValues, providerOptions, moduleOptions]);

  return config;
};
